import React, { useState, useEffect } from 'react';
import { useAuth } from 'src/contexts/AuthContext';
import { X } from 'lucide-react';
import { API_ENDPOINTS, getAuthHeaders, fetchUserProfile, UserProfileData } from 'src/services/api';
import { Button } from 'src/components/ui/Button';
import { Input } from 'src/components/ui/Input';
import { Label } from 'src/components/ui/Label';
import Preloader from 'src/components/Preloder';
import { toast } from 'react-toastify';
import { logger } from 'src/utils/logger';

// Custom password change function that doesn't auto-logout on 401
const changePasswordWithCustomErrorHandling = async (currentPassword: string, newPassword: string, confirmPassword: string) => {
    const response = await fetch(API_ENDPOINTS.auth.changePassword, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            current_password: currentPassword,
            new_password: newPassword,
            confirm_password: confirmPassword
        })
    });

    if (!response.ok) {
        const error = await response
            .json()
            .catch(() => ({ detail: 'An error occurred' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
};

// Profile Modal Component
export function ProfileModal({ onClose }: { onClose: () => void }) {
    const [showPasswordFields, setShowPasswordFields] = useState<boolean>(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [passwordSuccess, setPasswordSuccess] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const { user } = useAuth();

    const validatePasswordForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!currentPassword) {
            newErrors.currentPassword = 'Current password is required.';
        }

        if (!newPassword) {
            newErrors.newPassword = 'New password is required.';
        } else if (newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters long.';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(newPassword)) {
            newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm password is required.';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Confirm password must match the new password.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'currentPassword') {
            if (!value) {
                newErrors.currentPassword = 'Current password is required.';
            } else {
                delete newErrors.currentPassword;
            }
        } else if (fieldName === 'newPassword') {
            if (!value) {
                newErrors.newPassword = 'New password is required.';
            } else if (value.length < 8) {
                newErrors.newPassword = 'Password must be at least 8 characters long.';
            } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(value)) {
                newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
            } else {
                delete newErrors.newPassword;
            }

            // Also validate confirm password when new password changes
            if (confirmPassword && confirmPassword !== value) {
                newErrors.confirmPassword = 'Confirm password must match the new password.';
            } else if (confirmPassword && confirmPassword === value) {
                delete newErrors.confirmPassword;
            }
        } else if (fieldName === 'confirmPassword') {
            if (!value) {
                newErrors.confirmPassword = 'Confirm password is required.';
            } else if (newPassword !== value) {
                newErrors.confirmPassword = 'Confirm password must match the new password.';
            } else {
                delete newErrors.confirmPassword;
            }
        }

        setErrors(newErrors);
    };

    const toggleCurrentPasswordVisibility = () => {
        setShowCurrentPassword(!showCurrentPassword);
    };

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleChangePassword = () => {
        setShowPasswordFields(!showPasswordFields);
        setPasswordError(""); // Clear any previous errors
        setPasswordSuccess(""); // Clear any previous success messages
        setErrors({}); // Clear validation errors
    };

    const handlePasswordSubmit = async () => {
        setIsSubmitting(true);
        setPasswordError("");
        setPasswordSuccess("");
        setErrors({});

        // Validation using the new validation function
        if (!validatePasswordForm()) {
            setIsSubmitting(false);
            return;
        }

        try {
            // Make API call to update password
            await changePasswordWithCustomErrorHandling(currentPassword, newPassword, confirmPassword);

            // Password updated successfully
            toast.success("Password updated successfully!");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            setPasswordError("");

            // Auto-close the password fields after 1 second
            setTimeout(() => {
                setShowPasswordFields(false);
            }, 1000);
        } catch (error: any) {
            logger.error("Error updating password:", error);
            toast.error(error.message || "An error occurred while updating the password. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    {/* Header with close button */}
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-gray-800">Profile</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label="Close profile modal"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Profile Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* User Details Section */}
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-gray-800">User Details</h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-16-bold grey-500">User Name</label>
                                    <p className="text-18-bold grey-800 mb-4">{user?.full_name || 'Worker Name'}</p>
                                </div>

                                <div>
                                    <label className="text-16-bold grey-500">Email ID</label>
                                    <p className="text-18-bold grey-800 mb-4">{user?.email ?? 'worker@renegade.com'}</p>
                                </div>

                                <div>
                                    <label className="text-16-bold grey-500">User Role</label>
                                    <p className="text-18-bold grey-800 mb-4">{user?.role === 'admin' ? 'Administrator' : user?.role === 'manager' ? 'Manager' : 'Field Operator'}</p>
                                </div>

                                <div>
                                    <label className="text-16-bold grey-500">Joined On</label>
                                    <p className="text-18-bold grey-800 mb-4">Unknown</p>
                                </div>
                            </div>
                        </div>

                        {/* User Password Section */}
                        <div className="space-y-6">
                            {!showPasswordFields ? (
                                <>
                                    <h3 className="text-lg font-semibold text-gray-800">User Password</h3>
                                    <div className="space-y-4">
                                        <div className="text-18-bold grey-800 mb-4">••••••••</div>
                                        <Button onClick={handleChangePassword} variant="renegade" size="sm" className="px-4 py-2 w-fit">
                                            Change Password
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                    {/* Header with title and close */}
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-gray-800">User Password</h3>
                                        <button
                                            onClick={() => setShowPasswordFields(false)}
                                            className="text-gray-400 hover:text-gray-600 transition-colors"
                                            aria-label="Close password form"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>

                                    {/* Dashed divider */}
                                    <div className="border-t border-gray-200 border-dashed mb-6"></div>

                                    {/* Form Fields */}
                                    <div className="space-y-5">
                                        <div className="form-group password-feild">
                                            <label htmlFor="currentPassword">
                                                Current Password
                                            </label>
                                            <input
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                value={currentPassword}
                                                onChange={(e) => {
                                                    setCurrentPassword(e.target.value);
                                                    validatePasswordField('currentPassword', e.target.value);
                                                }}
                                                placeholder="Enter your current password"
                                                className={`form-control ${errors.currentPassword ? 'border-danger' : ''}`}
                                            />
                                            <button type="button" onClick={toggleCurrentPasswordVisibility} className='password-toggle'>
                                                {showCurrentPassword ? (
                                                    <i className="iconoir-eye"></i>
                                                ) : (
                                                    <i className="iconoir-eye-closed"></i>
                                                )}
                                            </button>
                                            {errors.currentPassword && (
                                                <p className="error-message">{errors.currentPassword}</p>
                                            )}
                                        </div>
                                        <div className="form-group password-feild">
                                            <label htmlFor="newPassword">
                                                New Password
                                            </label>
                                            <input
                                                type={showNewPassword ? 'text' : 'password'}
                                                value={newPassword}
                                                onChange={(e) => {
                                                    setNewPassword(e.target.value);
                                                    validatePasswordField('newPassword', e.target.value);
                                                }}
                                                placeholder="Enter your new password"
                                                className={`form-control ${errors.newPassword ? 'border-danger' : ''}`}
                                            />
                                            <button type="button" onClick={toggleNewPasswordVisibility} className='password-toggle'>
                                                {showNewPassword ? (
                                                    <i className="iconoir-eye"></i>
                                                ) : (
                                                    <i className="iconoir-eye-closed"></i>
                                                )}
                                            </button>
                                            {errors.newPassword && (
                                                <p className="error-message">{errors.newPassword}</p>
                                            )}
                                        </div>
                                        <div className="form-group password-feild">
                                            <label htmlFor="confirmPassword">
                                                Confirm Password
                                            </label>
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={confirmPassword}
                                                onChange={(e) => {
                                                    setConfirmPassword(e.target.value);
                                                    validatePasswordField('confirmPassword', e.target.value);
                                                }}
                                                placeholder="Enter your confirm password"
                                                className={`form-control ${errors.confirmPassword ? 'border-danger' : ''}`}
                                            />
                                            <button type="button" onClick={toggleConfirmPasswordVisibility} className='password-toggle'>
                                                {showConfirmPassword ? (
                                                    <i className="iconoir-eye"></i>
                                                ) : (
                                                    <i className="iconoir-eye-closed"></i>
                                                )}
                                            </button>
                                            {errors.confirmPassword && (
                                                <p className="error-message">{errors.confirmPassword}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Update Button */}
                                    <div className='d-flex justify-content-end mt-5 gap-15'>
                                        <button className='renegade-btn-secondary mini-btn' onClick={() => setShowPasswordFields(false)}>
                                            Cancel
                                        </button>
                                        <button className='renegade-btn-primary mini-btn' onClick={handlePasswordSubmit} disabled={isSubmitting}>
                                            {isSubmitting ? 'Updating...' : 'Update Password'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// Inline Profile View (for replacing chatbot area)
export function ProfileView({ onBack }: { onBack: () => void }) {
    const [showPasswordFields, setShowPasswordFields] = useState<boolean>(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [passwordSuccess, setPasswordSuccess] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [profileData, setProfileData] = useState<UserProfileData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");

    const { user } = useAuth();

    // Fetch user profile data
    useEffect(() => {
        const loadProfileData = async () => {
            try {
                setLoading(true);
                setError("");
                const data = await fetchUserProfile();
                setProfileData(data);
            } catch (err) {
                logger.error("Failed to fetch profile data:", err);
                setError("Failed to load profile data");
            } finally {
                setLoading(false);
            }
        };

        loadProfileData();
    }, []);

    const validatePasswordForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!currentPassword) {
            newErrors.currentPassword = 'Current password is required.';
        }

        if (!newPassword) {
            newErrors.newPassword = 'New password is required.';
        } else if (newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters long.';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(newPassword)) {
            newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm password is required.';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Confirm password must match the new password.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'currentPassword') {
            if (!value) {
                newErrors.currentPassword = 'Current password is required.';
            } else {
                delete newErrors.currentPassword;
            }
        } else if (fieldName === 'newPassword') {
            if (!value) {
                newErrors.newPassword = 'New password is required.';
            } else if (value.length < 8) {
                newErrors.newPassword = 'Password must be at least 8 characters long.';
            } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(value)) {
                newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
            } else {
                delete newErrors.newPassword;
            }

            // Also validate confirm password when new password changes
            if (confirmPassword && confirmPassword !== value) {
                newErrors.confirmPassword = 'Confirm password must match the new password.';
            } else if (confirmPassword && confirmPassword === value) {
                delete newErrors.confirmPassword;
            }
        } else if (fieldName === 'confirmPassword') {
            if (!value) {
                newErrors.confirmPassword = 'Confirm password is required.';
            } else if (newPassword !== value) {
                newErrors.confirmPassword = 'Confirm password must match the new password.';
            } else {
                delete newErrors.confirmPassword;
            }
        }

        setErrors(newErrors);
    };

    const toggleCurrentPasswordVisibility = () => {
        setShowCurrentPassword(!showCurrentPassword);
    };

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleChangePassword = () => {
        setShowPasswordFields(!showPasswordFields);
        setPasswordError("");
        setPasswordSuccess("");
        setErrors({});
    };

    const handlePasswordSubmit = async () => {
        setIsSubmitting(true);
        setPasswordError("");
        setPasswordSuccess("");
        setErrors({});

        // Validation using the new validation function
        if (!validatePasswordForm()) {
            setIsSubmitting(false);
            return;
        }

        try {
            // Make API call to update password
            await changePasswordWithCustomErrorHandling(currentPassword, newPassword, confirmPassword);

            // Password updated successfully
            toast.success("Password updated successfully!");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            setPasswordError("");

            // Auto-close the password fields after 1 second
            setTimeout(() => {
                setShowPasswordFields(false);
            }, 1000);
        } catch (error: any) {
            logger.error("Error updating password:", error);
            toast.error(error.message || "An error occurred while updating the password. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper function to format role display
    const formatRole = (role: string) => {
        switch (role.toLowerCase()) {
            case 'admin': return 'Administrator';
            case 'manager': return 'Manager';
            case 'worker': return 'Field Operator';
            default: return 'Field Operator';
        }
    };

    // Helper function to format date
    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return 'Unknown';
        }
    };

    if (loading) {
        return <Preloader />;
    }

    if (error) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 h-[calc(100vh-140px)] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onBack}
                            className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                            aria-label="Back"
                        >
                            <img src="/icons/backButton.png" alt="Back" className="w-4 h-4" />
                        </button>
                        <h2 className="text-xl font-bold text-gray-800">Profile</h2>
                    </div>
                </div>
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <p className="text-red-600 mb-4">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className='worker-profile'>
                <div className='worker-profile-header'>
                    <h3 className='grey-900 d-flex align-items-center gap-4'>
                        <button
                            onClick={onBack}
                            aria-label="Back"
                        >
                            <img src="/icons/backButton.png" alt="Back" className="w-4 h-4" />
                        </button>
                        Profile</h3>
                </div>
                <div className='worker-profile-content'>
                    <div className='worker-profile-details'>
                        <p className="worker-profile-details-title">User Details</p>
                        <div className="worker-profile-content-body">
                            <div>
                                <label className="text-16-bold grey-500">User Name</label>
                                <p className="text-18-bold grey-800 mb-4">{profileData?.full_name || user?.full_name || 'Worker Name'}</p>
                            </div>

                            <div>
                                <label className="text-16-bold grey-500">Email ID</label>
                                <p className="text-18-bold grey-800 mb-4">{(profileData?.email || user?.email) ?? 'worker@renegade.com'}</p>
                            </div>

                            <div>
                                <label className="text-16-bold grey-500">User Role</label>
                                <p className="text-18-bold grey-800 mb-4">{formatRole(profileData?.role || user?.role || 'worker')}</p>
                            </div>

                            <div>
                                <label className="text-16-bold grey-500">Manager Name</label>
                                <p className="text-18-bold grey-800 mb-4">{profileData?.assigned_manager_name || 'Not assigned'}</p>
                            </div>

                            <div>
                                <label className="text-16-bold grey-500">Joined On</label>
                                <p className="text-18-bold grey-800 mb-4">{profileData?.created_at ? formatDate(profileData.created_at) : 'Unknown'}</p>
                            </div>
                        </div>
                    </div>
                    <div className='worker-profile-password'>
                        <p className="worker-profile-details-title d-flex align-items-center justify-content-between">
                            <span>User Password</span>

                            {showPasswordFields && (
                                <button
                                    onClick={() => setShowPasswordFields(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    aria-label="Close password form"
                                >
                                    <X size={20} />
                                </button>
                            )}
                        </p>
                        {/* User Password Section */}
                        <div className="worker-profile-content-body">
                            {!showPasswordFields ? (
                                <>
                                    <button onClick={handleChangePassword} className='w-100 mini-btn renegade-btn-primary w-100'>
                                        Change Password
                                    </button>
                                </>
                            ) : (
                                <div>
                                    {/* Form Fields */}
                                    <div>
                                        <div className="form-group password-feild">
                                            <label htmlFor="currentPassword">
                                                Current Password
                                            </label>
                                            <input
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                value={currentPassword}
                                                onChange={(e) => {
                                                    setCurrentPassword(e.target.value);
                                                    validatePasswordField('currentPassword', e.target.value);
                                                }}
                                                placeholder="Enter your current password"
                                                className={`form-control ${errors.currentPassword ? 'border-danger' : ''}`}
                                            />
                                            <button type="button" onClick={toggleCurrentPasswordVisibility} className='password-toggle'>
                                                {showCurrentPassword ? (
                                                    <i className="iconoir-eye"></i>
                                                ) : (
                                                    <i className="iconoir-eye-closed"></i>
                                                )}
                                            </button>
                                            {errors.currentPassword && (
                                                <p className="error-message">{errors.currentPassword}</p>
                                            )}
                                        </div>
                                        <div className="form-group password-feild">
                                            <label htmlFor="newPassword">
                                                New Password
                                            </label>
                                            <input
                                                type={showNewPassword ? 'text' : 'password'}
                                                value={newPassword}
                                                onChange={(e) => {
                                                    setNewPassword(e.target.value);
                                                    validatePasswordField('newPassword', e.target.value);
                                                }}
                                                placeholder="Enter your new password"
                                                className={`form-control ${errors.newPassword ? 'border-danger' : ''}`}
                                            />
                                            <button type="button" onClick={toggleNewPasswordVisibility} className='password-toggle'>
                                                {showNewPassword ? (
                                                    <i className="iconoir-eye"></i>
                                                ) : (
                                                    <i className="iconoir-eye-closed"></i>
                                                )}
                                            </button>
                                            {errors.newPassword && (
                                                <p className="error-message">{errors.newPassword}</p>
                                            )}
                                        </div>
                                        <div className="form-group password-feild">
                                            <label htmlFor="confirmPassword">
                                                Confirm Password
                                            </label>
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={confirmPassword}
                                                onChange={(e) => {
                                                    setConfirmPassword(e.target.value);
                                                    validatePasswordField('confirmPassword', e.target.value);
                                                }}
                                                placeholder="Enter your confirm password"
                                                className={`form-control ${errors.confirmPassword ? 'border-danger' : ''}`}
                                            />
                                            <button type="button" onClick={toggleConfirmPasswordVisibility} className='password-toggle'>
                                                {showConfirmPassword ? (
                                                    <i className="iconoir-eye"></i>
                                                ) : (
                                                    <i className="iconoir-eye-closed"></i>
                                                )}
                                            </button>
                                            {errors.confirmPassword && (
                                                <p className="error-message">{errors.confirmPassword}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Update Button */}
                                    <button className='renegade-btn-primary mt-4 mb-4 w-100 mini-btn' onClick={handlePasswordSubmit} disabled={isSubmitting}>
                                        {isSubmitting ? 'Updating...' : 'Update Password'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// Utility function for formatting last login timestamp
export function formatLastLogin(iso?: string | null) {
    if (!iso) return 'Never';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'Never';
    return d.toLocaleString(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
}